/* eslint-disable no-extend-native */
// webpack emits this CommonJS module without a directive; strict mode keeps a
// null or undefined `this` from becoming `window`, so the TypeErrors below fire.
// eslint-disable-next-line strict
'use strict'

// T2-7361 / T2-7495: Android 12 devices can still run Android System WebView 91
// (Chrome 91), which lacks a few ES2022/ES2023 built-ins. Babel only
// transpiles syntax, and dependencies call some of these built-ins unguarded
// (react-markdown calls Object.hasOwn). This module loads first, for every
// session, and only defines a method when the browser lacks it.
//
// These are written out instead of importing core-js: in production builds
// babel-preset-react-app rewrites core-js entry imports for corejs 3.0, which
// predates these methods, so `require('core-js/actual/array/at')` compiles to
// nothing. The résumé renderer's PDF.js layer (pdfjsLegacyCompatibility.js)
// requires this module too, for the PDF worker's own global object; the
// typed-array `at` is here for PDF.js (T2-7112: WebKit before iOS 15.4 and
// WebView before Chrome 92 lack it).
//
// The methods are object shorthand, like the native ones: they keep their
// name after minification, have no `prototype` and cannot be called with `new`.
//
// T2-7539: the supported floor is Chrome/WebView 91 and iOS Safari 15
// (package.json browserslist `webview-floor`). `yarn lint` fails app code that
// uses the newer web APIs it knows about (eslint-plugin-compat plus a short
// no-restricted-syntax list) or ES2023+ built-ins (eslint-plugin-es-x). It is
// best-effort: compat has no data for some APIs, and dependencies are not
// linted. The methods defined here are allowed through package.json
// eslintConfig.settings.polyfills. To use another newer built-in, add it here,
// list it there for compat, turn off its es-x rule, and extend
// src/__tests__/webViewFloorLint.test.ts.

function define(target, name, method) {
	if (typeof target[name] === 'function') return
	Object.defineProperty(target, name, { value: method, writable: true, enumerable: false, configurable: true })
}

// ToIntegerOrInfinity; unary plus throws on BigInt and Symbol, as ToNumber does.
function toIntegerOrInfinity(value) {
	const number = +value
	if (Number.isNaN(number) || number === 0) return 0
	if (number === Infinity || number === -Infinity) return number
	return Math.trunc(number)
}

function lengthOf(object) {
	return Math.min(Math.max(toIntegerOrInfinity(object.length), 0), Number.MAX_SAFE_INTEGER)
}

function relativeIndex(index, length) {
	const relative = toIntegerOrInfinity(index)
	const k = relative >= 0 ? relative : length + relative
	return k < 0 || k >= length ? -1 : k
}

function toObject(value, method) {
	if (value === null || value === undefined) throw new TypeError(method + ' called on null or undefined')
	return Object(value)
}

function requireCallable(predicate) {
	if (typeof predicate !== 'function') throw new TypeError(predicate + ' is not a function')
}

const arrayMethods = {
	at(index) {
		const object = toObject(this, 'Array.prototype.at')
		const k = relativeIndex(index, lengthOf(object))
		return k === -1 ? undefined : object[k]
	},
	findLast(predicate) {
		const object = toObject(this, 'Array.prototype.findLast')
		const length = lengthOf(object)
		requireCallable(predicate)
		const thisArg = arguments[1]
		for (let k = length - 1; k >= 0; k--) {
			const value = object[k]
			if (predicate.call(thisArg, value, k, object)) return value
		}
		return undefined
	},
	findLastIndex(predicate) {
		const object = toObject(this, 'Array.prototype.findLastIndex')
		const length = lengthOf(object)
		requireCallable(predicate)
		const thisArg = arguments[1]
		for (let k = length - 1; k >= 0; k--) {
			if (predicate.call(thisArg, object[k], k, object)) return k
		}
		return -1
	},
}

const stringMethods = {
	at(index) {
		const string = String(toObject(this, 'String.prototype.at'))
		const k = relativeIndex(index, string.length)
		return k === -1 ? undefined : string.charAt(k)
	},
}

const objectMethods = {
	hasOwn(object, key) {
		return Object.prototype.hasOwnProperty.call(toObject(object, 'Object.hasOwn'), key)
	},
}

// Int8Array, Uint8Array, Float64Array and the rest share one prototype.
const typedArrayMethods = {
	at(index) {
		if (!ArrayBuffer.isView(this) || this instanceof DataView) {
			throw new TypeError('%TypedArray%.prototype.at called on a value that is not a typed array')
		}
		const k = relativeIndex(index, this.length)
		return k === -1 ? undefined : this[k]
	},
}

define(Array.prototype, 'at', arrayMethods.at)
define(String.prototype, 'at', stringMethods.at)
define(Array.prototype, 'findLast', arrayMethods.findLast)
define(Array.prototype, 'findLastIndex', arrayMethods.findLastIndex)
define(Object, 'hasOwn', objectMethods.hasOwn)
if (typeof Int8Array === 'function') define(Object.getPrototypeOf(Int8Array.prototype), 'at', typedArrayMethods.at)

module.exports = true
